import { _ } from "js/vendor";
import firebase from "js/firebase";
import { app } from "js/namespaces";
import { ds } from "./dataService";
import StorageModel from "../storage/storageModel";
import ReferenceCollection from "../storage/referenceCollection";
import StorageCollection from "../storage/storageCollection";
import FileStorage from "../storage/fileStorage";
import log from "../logger";
import {
    ASSET_RESOLUTION,
    ASSET_FILETYPE_TO_MIMETYPE,
    ASSET_ABBREVIATIONS,
    ASSET_MAX_AREA,
    AssetType,
    ASSET_SIZES,
    ASSET_TIMEOUT,
    ASSET_FILETYPE,
    ASSET_CACHE_TTL_SECONDS,
    ASSET_SIZE_REGEX,
    OLD_ASSET_SIZES,
    ASSET_AREAS
} from "common/constants";
import queueDispatch from "../utilities/queueDispatch";
import Profiler from "../profiler";
import { createHash } from "../utilities/utilities";
import { readImage, convertDataURItoBlob } from "js/core/utilities/imageUtilities";
import Api from "../api";
import { timeoutRace, loadImage, loadAndGetUrl } from "../utilities/promiseHelper";
import { isElectron } from "../../config";
import isConnected from "js/core/utilities/isConnected";
import { getAssetIds, migrateIconAssetId } from "common/assetUtils";

function resizeAsset(assetId) {
    return queueDispatch.assetResizer.send({
        taskId: assetId,
        assetId,
    }).catch(err => log.warn(err));
}

const profiler = Profiler.create({
    name: "Assets",
    type: Profiler.AVG_LOAD_TIME,
    warnTime: 400
});

const UserAssets = StorageCollection.extend({
    getQuery: function() {
        return {
            root: `userAssets/${app.user ? app.user.id : "anonymous"}`
        };
    },
    comparator: function(userAsset) {
        return userAsset.get("lastUsed");
    }
});

const Asset = StorageModel.extend({
    root: "assets",
    profiler: profiler,
    ensurePersisted: true,
    /* --
    Assets require skipReferenceCollectionLoad flag.
    There is a unique problem where we need to know the asset's type before instantiating a new Asset.
    Assets.prepareModel will fetch the data so we can create the right instance type in Assets.model
    However that Asset instance would normally then try to fetch its own data, which would mean fetching twice.
    skipReferenceCollectionLoad flag tells the referenceCollection (Assets) to skip its model load step.
    -- */
    skipReferenceCollectionLoad: true,

    get type() {
        return AssetType.IMAGE;
    },

    getIgnoredKeys() {
        return ["type"];
    },

    addAssetToUserAssets: function() {
        const userAsset = ds.userAssets.get(this.id);
        let workspaceId = "personal";
        if (ds.selection && ds.selection.presentation) {
            workspaceId = ds.selection.presentation.getWorkspaceId();
        } else {
            workspaceId = app.mainView.presentationsView.app.get("presentations").workspaceId;
        }
        if (userAsset) {
            userAsset.set({
                lastUsed: new Date().getTime(),
                workspaceIds: {
                    ...userAsset.get("workspaceIds"),
                    [workspaceId]: true
                }
            });
        } else {
            ds.userAssets.add({
                id: this.id,
                lastUsed: new Date().getTime(),
                workspaceIds: {
                    [workspaceId]: true
                }
            });
        }
    },

    applyMigrations: function(type) {
        const promises = [];
        type = type || this.get("hasSolidBackground") ? AssetType.LOGO : AssetType.IMAGE;
        const currentUser = firebase.auth().currentUser;
        const isOwner = currentUser && this.get("userId") === currentUser.uid;
        if (!this.has("type") && !(window.isPlayer || window.isRenderer)) {
            promises.push(
                Api.assets.put({ id: this.id, type })
                    .catch(err => {
                        // silently fail here in case of offline mode so getAssetById can still succeed.
                        // if type failed to be updated, we will just end up trying the migration again later.
                        log.error(err);
                    })
            );
        }

        // return a promise that resolves as the asset
        return Promise.all(promises).then(() => this);
    },

    canUseOriginal: function() {
        return false;
    },

    async toObject() {
        const attrs = await StorageModel.prototype.toObject.call(this);
        const promises = [];

        if (this.canUseOriginal()) {
            promises.push(this.getBaseUrl());
        }

        await Promise.all(promises);
        return attrs;
    },

    onReady: function() {
        if (!this.isInvalid()) {
            return Promise.resolve(this);
        } else {
            return timeoutRace(ASSET_TIMEOUT, new Promise((resolve, reject) => {
                this.once("change:original", () => {
                    resolve(this);
                });
            }));
        }
    },

    isInvalid: function() {
        const now = new Date();
        if (now.getTime() - this.get("createdAt") < ASSET_TIMEOUT) {
            return false;
        }

        return (!this.has("original"));
    },

    async getBaseUrl(suffix = null, ignoreCache = false) {
        log.info(log.ASSET)(`[${this.type}][${this.id}] getBaseUrl() suffix: ${suffix} ignoreCache: ${ignoreCache}`);

        const { url } = await Api.assets.get({ id: this.id, suffix }, ignoreCache);
        try {
            await loadImage(url);
        } catch (err) {
            if (ignoreCache) {
                throw err;
            }

            log.info(log.ASSET)(`[${this.type}][${this.id}] getBaseUrl() failed to load asset ${this.id}, fetching a new url without cache and trying again`);
            return this.getBaseUrl(suffix, true);
        }
        return url;
    }
});

const ImageAsset = Asset.extend({

    get type() {
        return AssetType.IMAGE;
    },

    getImageType: function() {
        if (this.has("imageType")) {
            return this.get("imageType");
        } else {
            // if (this.get("hasSolidBackground") || this.get("hasAlpha")){
            //     return AssetType.LOGO;
            // } else {
            return AssetType.IMAGE;
            // }
        }
    },

    // An asset is invalid if it took longer than 60 seconds to add the original url.
    isInvalid: function() {
        if (this.has("xsmall") || this.has("small") || this.has("medium") || this.has("large") || this.has("xlarge")) {
            return false;
        }
        return Asset.prototype.isInvalid.call(this);
    },

    uploadAsset: function(file, metadata) {
        const options = {
            path: "assets/" + this.id,
            file: file
        };
        metadata = Object.assign({
            contentType: ASSET_FILETYPE_TO_MIMETYPE[this.get("fileType")],
            cacheControl: "private, max-age=" + ASSET_CACHE_TTL_SECONDS
        }, metadata);
        return FileStorage.upload(options, metadata).then(result => {
            let updates = {
                id: this.id,
                original: result.downloadURL,
                hasResizedImages: false
            };
            return Api.assets.put(updates);
        }).then(() => {
            if (this.get("fileType") === "svg") return;
            return resizeAsset(this.id);
        }).then(() => {
            return this;
        });
    },

    generateLoadPromise: function() {
        return StorageModel.prototype.generateLoadPromise.call(this).then(() => {
            //safety regeneration of the assets if they are missing.
            if (this.has("original")) {
                if (Date.now() - this.get("createdAt") > ASSET_TIMEOUT) {
                    setTimeout(() => {
                        const area = this.get("h") * this.get("w");
                        const needsResize = !this.get("invalidAsset") && Object.keys(ASSET_SIZES).reduce((needsResize, key) => {
                            const sizeName = ASSET_SIZES[key];
                            const size = parseInt(key);
                            return needsResize || (area > size && !this.has(sizeName));
                        }, false);
                        if (needsResize && !(this.get("fileType") === "svg")) {
                            resizeAsset(this.id);
                        }
                    }, ASSET_TIMEOUT);
                }
            }
            return this;
        });
    },

    getURL: async function(size) {
        log.info(log.ASSET)(`[${this.type}][${this.id}] getURL() size: ${size}`);

        // if the image hasn't been uploaded yet, return the tempImage from the upload process
        if (this.tempImage) {
            log.info(log.ASSET)(`[${this.type}][${this.id}] getURL() using tempImage value as url ${this.tempImage}`);
            return this.tempImage;
        }

        if (this.get("fileType") === ASSET_FILETYPE.SVG || this.get("fileType") === ASSET_FILETYPE.GIF) {
            size = "original";
        }

        const sizes = Object.keys(ASSET_RESOLUTION).map(key => ASSET_RESOLUTION[key]);
        if (this.canUseOriginal()) {
            sizes.push("original");
        }

        let start = sizes.indexOf(size);
        // if the size is not found, use the last size
        if (start === -1) {
            start = sizes.length - 1;
        }

        //Find size or larger...
        for (let i = start; i < sizes.length; i++) {
            const size = sizes[i];
            const storedUrl = this.get(size);
            if (storedUrl != null) {
                if (size === "original") {
                    log.info(log.ASSET)(`[${this.type}][${this.id}] getURL() using original`);
                    return this.getBaseUrl();
                }
                const maxArea = this.getMaxAreaMatch(storedUrl, size);
                log.info(log.ASSET)(`[${this.type}][${this.id}] getURL() requesting ${maxArea} suffix for ${size} size`);
                return this.getBaseUrl(maxArea);
            }
        }

        //Find smaller sizes...
        for (let i = start - 1; i >= 0; i--) {
            const size = sizes[i];
            const storedUrl = this.get(size);
            if (storedUrl != null) {
                if (size === "original") {
                    log.info(log.ASSET)(`[${this.type}][${this.id}] getURL() using original`);
                    return this.getBaseUrl();
                }
                const maxArea = this.getMaxAreaMatch(storedUrl, size);
                log.info(log.ASSET)(`[${this.type}][${this.id}] getURL() requesting ${maxArea} suffix for ${size} size`);
                return this.getBaseUrl(maxArea);
            }
        }

        //The asset is broken, show the original
        log.info(log.ASSET)(`[${this.type}][${this.id}] getURL() using original`);
        return this.getBaseUrl();
    },

    getMaxAreaMatch(storedUrl, size) {
        let match = new URL(storedUrl).pathname.match(ASSET_SIZE_REGEX);
        if (match && match[1] && OLD_ASSET_SIZES.find(size => size === match[1])) {
            // check if image uses old asset sizes
            return match[1];
        } else {
            // assume asset uses new sizes
            return ASSET_AREAS[size];
        }
    },

    canUseOriginal: function() {
        if (this.get("h") * (this.get("w")) <= ASSET_MAX_AREA) {
            return true;
        }
        return !Object.keys(ASSET_RESOLUTION).reduce((result, key) => {
            return result || this.has(ASSET_RESOLUTION[key]);
        }, false);
    },

    async toObject() {
        const attrs = await StorageModel.prototype.toObject.call(this);

        const sizes = Object.keys(ASSET_RESOLUTION).map(key => ASSET_RESOLUTION[key]);

        const promises = sizes.filter(size => this.get(size)).map(size => {
            let storedUrl = this.get(size);
            let maxArea = this.getMaxAreaMatch(storedUrl, size);
            return this.getBaseUrl(maxArea);
        });

        if (this.canUseOriginal()) {
            promises.push(this.getBaseUrl());
        }

        await Promise.all(promises);

        return attrs;
    },

});

const LogoAsset = ImageAsset.extend({
    get type() {
        return AssetType.LOGO;
    },
});

const IconAsset = Asset.extend({
    get type() {
        return AssetType.ICON;
    },

    canUseOriginal() {
        return false;
    },

    async toObject() {
        const attrs = await StorageModel.prototype.toObject.call(this);
        const promises = [];
        // icon assets previously had the svg in the model, now the svg is fetched from storage and stored as text
        let url = this.get("original");
        promises.push(fetch(url).then(res => res.text()));

        await Promise.all(promises);
        return attrs;
    }
});

const AudioAsset = Asset.extend({

    get type() {
        return AssetType.AUDIO;
    },

    uploadAsset: function(file, metadata, onProgress) {
        const options = {
            path: "assets/" + this.id,
            file: file,
            onProgress: onProgress
        };
        metadata = Object.assign({
            contentType: file.type,
            cacheControl: "private, max-age=" + ASSET_CACHE_TTL_SECONDS
        }, metadata);
        return FileStorage.upload(options, metadata).then(result => {
            this.update({ original: result.downloadURL });
        });
    },

    canUseOriginal: function() {
        return true;
    },

    async getBaseUrl(ignoreCache = false) {
        log.info(log.ASSET)(`[${this.type}][${this.id}] getBaseUrl() ignoreCache: ${ignoreCache}`);

        const { url } = await Api.assets.get({ id: this.id }, ignoreCache);
        try {
            const resp = await fetch(url);
            if (resp.status >= 400) {
                throw new Error(`Resource responded with ${resp.status} status`);
            }
        } catch (err) {
            if (ignoreCache) {
                throw err;
            }

            log.info(log.ASSET)(`[${this.type}][${this.id}] getBaseUrl() failed to load asset ${this.id}, fetching a new url without cache and trying again`);
            return this.getBaseUrl(true);
        }
        return url;
    }
});

const FontAsset = Asset.extend({

    get type() {
        return AssetType.FONT;
    },

    uploadAssets: function(fonts, metadata) {
        const promises = fonts.map(font => {
            const file = font.file;
            if (file) {
                const options = {
                    path: `assets/${this.id}-${font.type}`,
                    file: file
                };
                metadata = Object.assign({
                    contentType: file.type,
                    cacheControl: "private, max-age=" + ASSET_CACHE_TTL_SECONDS
                }, metadata);
                return FileStorage.upload(options, metadata);
            } else {
                return Promise.resolve();
            }
        });
        return Promise.all(promises).then(([regular, italic, bold, bolditalic, image]) => {
            const urls = {
                regular: regular.downloadURL,
                italic: italic ? italic.downloadURL : null,
                bold: bold ? bold.downloadURL : null,
                bolditalic: bolditalic ? bolditalic.downloadURL : null,
                image: image.downloadURL
            };
            this.update(urls);
        });
    },

    canUseOriginal: function() {
        return false;
    },

    async getBaseUrl(fontType, ignoreCache = false) {
        log.info(log.ASSET)(`[${this.type}][${this.id}] getBaseUrl() fontType: ${fontType} ignoreCache: ${ignoreCache}`);

        let suffix;
        switch (fontType) {
            case "regular":
                suffix = "Regular";
                break;
            case "italic":
                suffix = "Italic";
                break;
            case "bold":
                suffix = "Bold";
                break;
            case "bolditalic":
                suffix = "Bold Italic";
                break;
            default:
                suffix = "";
                break;
        }

        log.info(log.ASSET)(`[${this.type}][${this.id}] getBaseUrl() calculated suffix ${suffix} for ${fontType} font type`);
        const { url } = await Api.assets.get({ id: this.id, suffix }, ignoreCache);
        try {
            const resp = await fetch(url);
            if (resp.status >= 400) {
                throw new Error(`Resource responded with ${resp.status} status`);
            }
        } catch (err) {
            if (ignoreCache) {
                throw err;
            }

            log.info(log.ASSET)(`[${this.type}][${this.id}] getBaseUrl() failed to load asset ${this.id}, fetching a new url without cache and trying again`);
            return this.getBaseUrl(fontType, true);
        }
        return url;
    },

    async toObject() {
        const attrs = await StorageModel.prototype.toObject.call(this);

        const fontTypes = ["regular", "italic", "bold", "bolditalic"].filter(fontType => this.get(fontType));

        await Promise.all(fontTypes.map(fontType => this.getBaseUrl(fontType)));

        return attrs;
    },
});

const VideoAsset = Asset.extend({
    get type() {
        return AssetType.VIDEO;
    },

    canUseOriginal() {
        return false;
    },

    async toObject() {
        if (!isElectron) {
            throw new Error("toObject() can only be called in electron environment");
        }

        // This url will be used by the main process to download the video
        const { url } = await Api.assets.get({ id: this.id }, true);

        // Calling the main process to save the video into ./assets/video folder by emitting [window.saveVideoAssetEventType] event
        await new Promise((resolve, reject) => {
            // Subsribing to the reply event because the saving process is asynchronous
            const replyEventType = `${window.saveVideoAssetEventType}-reply-${this.id}`;
            document.addEventListener(replyEventType, event => {
                if (event.detail.error) {
                    reject(event.detail.error);
                    return;
                }

                resolve();
            });

            // Emitting [window.saveVideoAssetEventType] event
            document.dispatchEvent(new CustomEvent(window.saveVideoAssetEventType, { detail: { videoUrl: url, videoAssetId: this.id } }));
        });

        const modelObject = await StorageModel.prototype.toObject.call(this);
        // This url is pointing to the static assets folder that is served by app:// protocol (see service-worker.js)
        // and actually returns local files from ./assets/ folder
        modelObject.url = `${window.staticUrl}/videos/${encodeURIComponent(this.id)}`;
        return modelObject;
    },

    async getURL() {
        log.info(log.ASSET)(`[${this.type}][${this.id}] getURL()`);

        if (isElectron) {
            if (!this.loaded) {
                await this.load();
            }

            // If url is defined then the video is saved locally and we can use
            // it both while offline and online
            const localVideoFileUrl = this.get("url");
            if (localVideoFileUrl) {
                return localVideoFileUrl;
            }

            // If we're offline and the video is now saved locally - throwing an error
            if (!isConnected.connected) {
                throw new Error("Video is not available offline");
            }
        }

        const { url } = await Api.assets.get({ id: this.id }, true);
        return url;
    }
});

const Assets = ReferenceCollection.extend({
    type: "Assets",
    ignoreErrors: true,
    comparator: "createdAt",

    model: function(attrs, options) {
        switch (attrs.type) {
            case "audio":
                return new AudioAsset(attrs, options);
            case "logo":
                return new LogoAsset(attrs, options);
            case "icon":
                return new IconAsset(attrs, options);
            case "font":
                return new FontAsset(attrs, options);
            case "video":
                return new VideoAsset(attrs, options);
            case "image":
            default:
                return new ImageAsset(attrs, options);
        }
    },

    prepareModel: async function(model) {
        return await StorageModel.fetchData(model.id, { root: "assets" });
    },

    referenceRoot: "userAssets",
    getReferenceId: function() {
        return app.user ? app.user.id : "anonymous";
    },

    getAssetById: function(assetId, type) {
        let promise;
        //use default icon name if the type is an icon and the assetId is not the new icon- format.
        if (type === "icon") {
            assetId = migrateIconAssetId(assetId, app.currentTheme, app);
        }
        if (this.has(assetId)) {
            promise = Promise.resolve(this.get(assetId));
        } else {
            promise = this.add({ id: assetId }, { remoteChange: true });
        }
        return promise.then(asset => {
            if (asset) {
                return asset.applyMigrations(type);
            } else {
                return Promise.reject(`ASSET ERROR - id: ${assetId}, type: ${type}`);
            }
        });
    },

    getAssets: function(assetIds, type) {
        return Promise.all(assetIds.map(assetId => this.getAssetById(assetId, type)));
    },

    getAssetsByType(type, orgId) {
        const workspaceId = orgId || "personal";
        return ds.userAssets.load().then(() => {
            return ds.userAssets
                .filter(asset => {
                    const workspaceIds = asset.get("workspaceIds") || { personal: true };
                    return workspaceIds[workspaceId];
                })
                .map(asset => asset.id);
        }).then(ids => {
            return this.getAssets(ids, type);
        }).then(assets => {
            return assets.filter(asset => asset.get("type") === type);
        });
    },

    loadAssetsForSlide: function(slide) {
        let assets = getAssetIds(slide.toJSON()).filter(
            ({ id }) => !this.has(id));
        return this.add(assets, { remoteChange: true }).catch(e => {
            log.warn(e);
        });
    },

    //Note: this will eventually update the assets list.
    createAsset: function(data) {
        return this.add(Object.assign({
            createdAt: new Date().getTime(),
            userId: app.user.id
        }, data), { loadModels: false });
    },

    getOrCreateImage: function({ file, fileType, url, assetType, name, metadata = {}, imageProperties = {}, hidden }) {
        if (file) {
            // if passed a file, hash it and load asset with that hash as an id
            // if asset doesn't yet exist, create the asset

            return readImage(file, assetType)
                .then(({ file, imageProps, dataUrl }) => {
                    let hash = createHash(dataUrl, "BYTES");
                    return new ImageAsset({ id: `${hash}-${ASSET_ABBREVIATIONS[assetType]}` })
                        // This will throw an error with a 404 status code if an asset with the given id does not exist
                        .load()
                        // Asset already exists
                        .then(asset => {
                            // If the asset is invalid, replace it
                            if (asset.isInvalid()) {
                                this.add(asset, { silent: true, loadModels: false });
                                asset.tempImage = dataUrl;
                                return asset.uploadAsset(file, {
                                    customMetadata: {
                                        source: "user",
                                        userId: app.user.id
                                    }
                                });
                            }
                            return asset;
                        })
                        .catch(err => {
                            // Asset with the given id was not found, creating a new one
                            if (err.statusCode === 404) {
                                imageProps = Object.assign({}, imageProps, imageProperties);
                                return this.loadFileAndCreateAsset(assetType, dataUrl, file, name || file.name, fileType, metadata, imageProps, hash);
                            } else {
                                throw Error(err);
                            }
                        })
                        .then(asset => {
                            asset.addAssetToUserAssets();
                            return asset;
                        });
                });
        } else if (url) {
            // if passed a url, hash it and load asset with that hash as an id
            // if asset doesn't yet exist, create the asset
            let hash = createHash(url);
            return new ImageAsset({ id: `${hash}-${ASSET_ABBREVIATIONS[assetType]}` })
                // This will throw an error with a 404 status code if an asset with the given id does not exist
                .load()
                // Asset already exists
                .then(asset => {
                    // If the asset does not have the "original" property (the actual file wasn't uploaded to the storage)
                    // then assume the asset doesn't exist
                    if (!asset.has("original")) {
                        const error = new Error("No original property");
                        error.statusCode = 404;
                        throw error;
                    }
                    return asset;
                })
                .catch(err => {
                    // Asset with the given id was not found, creating a new one
                    if (err.statusCode === 404) {
                        return fetch(url)
                            .then(resp => resp.blob())
                            .then(blob => ds.assets.loadFileAndCreateAsset(assetType, url, blob, name, fileType, metadata, imageProperties, hash));
                    } else {
                        throw Error(err);
                    }
                })
                .then(asset => {
                    if (!hidden) {
                        asset.addAssetToUserAssets();
                    }
                    return asset;
                });
        } else {
            return Promise.reject(new Error("Error: File or URL required to get asset."));
        }
    },

    loadFileAndCreateAsset: function(assetType, dataURL, file, fileName, fileType, metadata, imageProperties = {}, hash) {
        return loadImage(dataURL).then(async img => {
            const asset = await this.createAsset({
                id: `${hash}-${ASSET_ABBREVIATIONS[assetType]}`,
                type: assetType,
                w: img.naturalWidth || imageProperties.width,
                h: img.naturalHeight || imageProperties.height,
                name: fileName,
                fileType: fileType,
                source: metadata.source,
                attribution: metadata.attribution || "",
                link: metadata.link || "",
                hasAlpha: imageProperties.hasAlpha || false,
                hasSolidBackground: imageProperties.hasSolidBackground || false,
                imageBackgroundColor: imageProperties.imageBackgroundColor || false
            });
            await asset.load();

            // store a temporary reference to the image for use until the actual image is uploaded
            asset.tempImage = dataURL;

            // Due to issues where a failed upload wont alert the users, we will wait until the upload finishes before
            // progressing.
            return asset.uploadAsset(file, {
                customMetadata: {
                    source: "user",
                    userId: app.user.id
                }
            });
        });
    }

});

export {
    UserAssets,
    Asset,
    Assets,
    ImageAsset,
    IconAsset,
    AudioAsset,
};
